import { cn } from "@assessment/utils/cn";
import GetHeroIcon from "@assessment/utils/getHeroIcons";
import { ProctoringFailureReason } from "@assessment/types/proctoring";
import { useTranslation } from "react-i18next";

interface CalibrationProcessStatusAlertProps {
  status: string;
  failureReason?: ProctoringFailureReason | null;
  timeout?: boolean;
}
const CalibrationProcessStatusAlert = ({
  status,
  failureReason,
  timeout
}: CalibrationProcessStatusAlertProps) => {
  const { t } = useTranslation();

  const retryReasonMessage =
    failureReason === "camera_permission_denied"
      ? t("assessment.systemCheck.failureReasons.cameraPermissionDenied")
      : failureReason === "camera_unavailable"
        ? t("assessment.systemCheck.failureReasons.cameraUnavailable")
        : failureReason === "facemesh_init_failed"
          ? t("assessment.systemCheck.failureReasons.facemeshInitFailed")
          : failureReason === "coco_load_failed"
            ? t("assessment.systemCheck.failureReasons.cocoLoadFailed")
            : failureReason === "stream_lost_reacquire_failed"
              ? t(
                  "assessment.systemCheck.failureReasons.streamLostReacquireFailed"
                )
              : failureReason === "calibration_insufficient_data"
                ? t(
                    "assessment.systemCheck.failureReasons.calibrationInsufficientData"
                  )
                : failureReason === "detection_cadence_unavailable"
                  ? t(
                      "assessment.systemCheck.failureReasons.detectionCadenceUnavailable"
                    )
                  : "";

  return (
    <div
      className={cn(
        "flex px-5 py-3 gap-3 rounded-r-md border-l-4 border-l-[#fbbf24] text-[#92400E] bg-[#FFFBEB]",
        status === "Calibrating" && timeout && "border-l-red-500 bg-red-100",
        status === "Retry required" &&
          "border-l-amber-500 bg-amber-50 text-amber-700",
        status === "Proctoring unavailable" &&
          "border-l-red-500 bg-red-100 text-red-700",
        status === "Calibration completed" &&
          "border-[#00875D] text-[#00875D] bg-[#F0FDF4]"
      )}
    >
      <GetHeroIcon
        name={
          timeout && status === "Calibrating"
            ? "ExclamationTriangleIcon"
            : status === "Retry required" || status === "Proctoring unavailable"
              ? "ExclamationTriangleIcon"
              : "ExclamationCircleIcon"
        }
        variant="solid"
        className={cn(
          "w-5 h-5  text-[#fbbf24]",
          timeout && "mt-2 text-red-500",
          (status === "Retry required" ||
            status === "Proctoring unavailable") &&
            "text-red-500",
          status === "Calibration completed" && "text-[#00875D]"
        )}
      />
      <div>
        <span className="text-sm font-medium">
          {status === "Ready for calibration" &&
            t("assessment.systemCheck.statusMessages.readyForCalibration")}
          {status === "Calibrating" &&
            !timeout &&
            t("assessment.systemCheck.statusMessages.calibratingInProgress")}
          {status === "Calibrating" &&
            timeout &&
            t("assessment.systemCheck.statusMessages.calibratingTimedOut")}
          {status === "Preparing camera preview" &&
            t("assessment.systemCheck.statusMessages.preparingCameraPreview")}
          {status === "Retry required" &&
            t("assessment.systemCheck.statusMessages.retryRequired")}
          {status === "Proctoring unavailable" &&
            t("assessment.systemCheck.statusMessages.proctoringUnavailable")}
          {status === "Calibration completed" &&
            t("assessment.systemCheck.statusMessages.calibrationCompleted")}
        </span>
        {status === "Retry required" && retryReasonMessage && (
          <div className="text-sm">{retryReasonMessage}</div>
        )}
        {timeout && (
          <div className="text-sm">
            {t("assessment.systemCheck.statusMessages.timeoutHelp")}
          </div>
        )}
      </div>
    </div>
  );
};

export default CalibrationProcessStatusAlert;
